import Site from 'es/app/business-logic/domain-models/Site';
import Throbber from 'es/app/components/progress-indicators/Throbber';
import { useUserStore } from 'es/app/store/GlobalStore';
import { useSiteFromUrl } from 'es/app/views/site/RouteMap';
import { useI18n } from 'es/core/hooks/useI18n';
import { observer } from 'mobx-react';
import { PropsWithChildren, useEffect, useMemo } from 'react';
import Authenticator from './Authenticator';

const Protected = (props: PropsWithChildren<Record<never, never>>) => {
    const { l10n } = useI18n('app', 'activity.fetching');
    const store = useUserStore();

    useAccessibleSites();
    useUserDetails();
    useActiveSite();

    const loadingText = useMemo(() => {
        if (!store.accessibleFacilities) return l10n('facilities', { suffix: '...' });
        if (!store.loggedInUser) return l10n('userDetails', { suffix: '...' });
    }, [store.accessibleFacilities, store.loggedInUser, l10n]);

    if (!!loadingText) {
        return <Throbber visible={true} text={loadingText} />;
    }

    return <>{props.children}</>;
};

function useAccessibleSites() {
    const store = useUserStore();

    useEffect(() => {
        if (store.accessibleFacilities) return;
        (async () => {
            const { companies, facilities } = await Authenticator.fetchAccessibleSites();
            if (!companies) return;
            store.setAccessibleFacilities(companies, facilities);
        })();
    }, [store, store.accessibleFacilities]);
}

function useUserDetails() {
    const store = useUserStore();
    useEffect(() => {
        if (store.loggedInUser || !store.accessibleFacilities) return;
        (async () => {
            const userDetails = await Authenticator.fetchUserDetails();
            if (!userDetails) return;
            store.setLoggedInUser(userDetails);
        })();
    }, [store, store.accessibleFacilities, store.loggedInUser]);
}

function useActiveSite() {
    const store = useUserStore();
    const urlSite = useSiteFromUrl();
    useEffect(() => {
        if (!store.accessibleFacilities) return;
        if (store.activeSite && store.activeSite.key === urlSite.key) return;
        if (urlSite.key === Site.Global.key && store.activeSite) {
            return;
        }
        const facility =
            store.accessibleFacilities.find(f => f.site.key === urlSite.key) ?? store.accessibleFacilities[0];
        if (facility?.site ?? null !== store.activeSite) {
            if (!facility) return;
            store.setActiveFacility(facility);
        }
    }, [store, store.activeSite, store.accessibleFacilities, urlSite.key]);
}

export default observer(Protected);
